import type { ComboSummary } from '@erp/shared';
import { Package, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Badge, Card, Skeleton } from '../../components/shadcn/index.js';
import { Button, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatMoney } from '../../lib/money.js';

/**
 * Read-only index of every bundle. The EDITOR is the product's own Bundle tab —
 * one editor for one entity — so a row click (and the empty state) deep-link
 * there rather than duplicating the grid here.
 */
export function CombosPage(): JSX.Element {
  const { t, i18n } = useTranslation('combos');
  const navigate = useNavigate();
  const [rows, setRows] = useState<ComboSummary[] | null>(null);

  useEffect(() => {
    void api().get<{ items: ComboSummary[] }>('/combos')
      .then((r) => setRows(r.data.items))
      .catch(() => setRows([]));
  }, []);

  if (!rows) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;

  const columns: DataTableColumn<ComboSummary>[] = [
    {
      id: 'item',
      header: t('page.bundle', { defaultValue: 'Bundle' }),
      cell: (r) => (
        <span>
          <span className="font-mono text-xs text-fg-muted">{r.itemCode}</span> · <span className="font-medium">{r.itemName}</span>
        </span>
      ),
      sortValue: (r) => r.itemCode,
      searchValue: (r) => `${r.itemCode} ${r.itemName}`,
    },
    {
      id: 'components',
      header: t('page.components', { defaultValue: 'Products inside' }),
      align: 'end',
      cell: (r) => r.componentCount,
      sortValue: (r) => r.componentCount,
    },
    {
      id: 'price',
      header: t('page.price', { defaultValue: 'Bundle price' }),
      align: 'end',
      cell: (r) => (r.salePrice != null && r.saleCurrencyCode
        ? <span className="font-mono">{formatMoney(r.salePrice, { locale: i18n.language, currency: r.saleCurrencyCode })}</span>
        : <span className="text-fg-muted">—</span>),
      sortValue: (r) => Number(r.salePrice ?? 0),
    },
    {
      id: 'buildable',
      // The bundle keeps no stock of its own, so "how many can I sell?" is a
      // question about its emptiest component — worth its own column.
      header: t('page.buildable', { defaultValue: 'Can sell now' }),
      align: 'end',
      cell: (r) => (
        <span className={Number(r.buildable) === 0 ? 'font-mono text-danger' : 'font-mono'}>{r.buildable}</span>
      ),
      sortValue: (r) => Number(r.buildable),
    },
    {
      id: 'active',
      header: t('page.active', { defaultValue: 'Active' }),
      cell: (r) => (
        <Badge variant={r.isActive ? 'success' : 'secondary'}>
          {r.isActive ? t('page.activeYes', { defaultValue: 'Active' }) : t('page.activeNo', { defaultValue: 'Off' })}
        </Badge>
      ),
      sortValue: (r) => (r.isActive ? 1 : 0),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Package className="h-6 w-6 text-brand" aria-hidden />
            {t('page.title', { defaultValue: 'Bundles' })}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            {t('page.subtitle', { defaultValue: 'Sets sold at one price. A bundle is edited on the product’s own Bundle tab.' })}
          </p>
        </div>
        {/* A bundle IS a product, so "new bundle" is the product form opened on
            its Bundle tab — one flow, rather than "go make a product first". */}
        <Button type="button" onClick={() => navigate('/inventory/items/new?tab=combo')}>
          <Plus className="me-1.5 h-4 w-4" aria-hidden />
          {t('page.create', { defaultValue: 'New bundle' })}
        </Button>
      </header>

      {rows.length === 0 ? (
        <EmptyState
          icon={<Package className="h-8 w-8" aria-hidden />}
          title={t('page.empty', { defaultValue: 'No bundles yet' })}
          description={t('page.emptyHint', { defaultValue: 'Name the bundle, give it a price, then add the products that go inside — all on one page.' })}
          action={(
            <Button type="button" onClick={() => navigate('/inventory/items/new?tab=combo')}>
              <Plus className="me-1.5 h-4 w-4" aria-hidden />
              {t('page.create', { defaultValue: 'New bundle' })}
            </Button>
          )}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable
            data={rows}
            columns={columns}
            rowKey={(r) => r.itemId}
            searchable
            onRowClick={(r) => navigate(`/inventory/items/${r.itemId}/edit?tab=combo`)}
            initialSort={{ id: 'item', dir: 'asc' }}
          />
        </Card>
      )}
    </div>
  );
}
